import React from "react";
import APIService from "../../Service/APIService";
import { Button, Input, Badge, Tabs, Pagination } from "antd";
import {
  ExclamationCircleFilled,
  CheckCircleFilled,
  FileDoneOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import moment from "moment";
import DateRangeFilter from "./DateRangeFilter";
import Swal from "sweetalert2";
import TitleBar from "../../common/title-bar/title-bar.js";
import CommonButton from "../../common/button/button.js";
import Table from "../../common/table/index.js";
import TableLink from "../../common/table/table-link/index.js";
import StatusIndicator from "../../common/status-indicator/index.js";
import { useScreen } from "../Utils/Screens.js";
import { colors } from "../../theme/colors.js";
import InvoiceFormNew from "../InvoiceForm/InvoiceFormNew.js";
import TableOptionsCellMenu from "../../common/table/table-options-cell/table-options-cell-menu.js";
import AddTrackingModal from "./AddTrackingModal.js";
import TabFade from "../../common/tab-fade";

import { useAuth } from '../AuthProvider.js';
import { hasPermission } from "../../Config/permissions.js";
const { TabPane } = Tabs;

const ORDER_FILTERS = [
  {
    value: "Approved",
    label: "Approved",
    icon: <CheckCircleFilled style={{ color: "#27a907" }} />,
  },
  {
    value: "Shipped",
    label: "Shipped",
    icon: <CheckCircleFilled style={{ color: colors["primaryColor"] }} />,
  },
  {
    value: "Rejected",
    label: "Rejected",
    icon: <ExclamationCircleFilled style={{ color: "#ff0000" }} />,
  },
  {
    value: "Payment Failed",
    label: "Payment Failed",
    icon: <ExclamationCircleFilled style={{ color: "#ff0000" }} />,
  },
  {
    value: "Completed",
    label: "Completed",
    icon: <FileDoneOutlined style={{ color: colors["primaryColor"] }} />,
  },
  {
    value: "Pending",
    label: "Pending",
  },
];

const ORDER_SORT_FIELDS = new Set([
  "PONumber",
  "ShipToAccountName",
  "ManufacturerName",
  "OrderDate",
  "DistributorName",
  "OrderTotal",
]);

const Orders = () => {
  const { user } = useAuth();
  const userData = user;
  const [activeTab, setActiveTab] = React.useState("DirectOrders");
  const [show] = React.useState(true);
  const [tabData, setTabData] = React.useState({
    DirectOrders: [],
    ConsignmentOrders: [],
    ProductUsage: [],
  });
  const [pagination, setPagination] = React.useState({
    DirectOrders: { page: 1, pageSize: 20, total: 0 },
    ConsignmentOrders: { page: 1, pageSize: 20, total: 0 },
    ProductUsage: { page: 1, pageSize: 20, total: 0 },
  });
  const [sortStateByTab, setSortStateByTab] = React.useState({
    DirectOrders: null,
    ConsignmentOrders: null,
    ProductUsage: null,
  });
  const [showInvoiceModal, setShowInvoiceModal] = React.useState(false);
  const [showAddTrackingModal, setShowAddTrackingModal] = React.useState(false);
  const [orderData, setOrderData] = React.useState(null);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [dateRange, setDateRange] = React.useState(null);
  const requestIdRef = React.useRef(0);

  const isPhone = !useScreen("sm");

  const hasTrackingNumber = (value) => String(value ?? "").trim().length > 0;

  const getTableOptionsCellProps = (
    row,
    userData,
    openInvoiceModal,
    openAddTrackingModal
  ) => {
    const {
      OrderType,
      OrderStatus,
      InvoiceID,
      ID,
      IsInvoiceGenerated,
      TrackingNumber,
    } = row;

    const props = {
      rowData: row,
    };

    // if (["Direct Order", "DO"].includes(OrderType)) {
    //   props.optionOne = "View";
    //   props.optionOneType = "warning";
    //   props.optionOneClickPath = `/DirectOrder/${ID}`;
    //   // props.optionOneClickFn = () => setLinkChange?.(true);
    // }

    // if (["Consignment Order", "CO"].includes(OrderType)) {
    //   props.optionTwo = "View";
    //   props.optionTwoType = "warning";
    //   props.optionTwoClickPath = `/ConsignmentOrder/${ID}`;
    //   // props.optionTwoClickFn = () => setLinkChange?.(true);
    // }

    // if (["Product Usage", "PU"].includes(OrderType)) {
    //   props.optionThree = "View";
    //   props.optionThreeType = "warning";
    //   props.optionThreeClickPath = `/ProductUsage/${ID}`;
    //   // props.optionThreeClickFn = () => setLinkChange?.(true);
    // }

    // if (OrderStatus === "Completed" && userData?.Type !== "Viewer") {

    // props.isActionsMenuOpen = isActionsMenuOpen;
    // props.setIsActionsMenuOpen = setIsActionsMenuOpen;
    props.actionOption = "Actions"
    props.actionOptionType = "primary"


    if (OrderStatus === "Completed") {
      props.optionThree =
        OrderType === "Consignment Order" ? "View Details" : "View Invoice";
      props.optionThreeType = "warning";
      props.optionThreeClickPath = `/InvoiceHome/${InvoiceID}`;
      // props.optionFourClickFn = () => setLinkChange?.(true);
    }

    if ((OrderStatus === "Approved" || OrderStatus === "Shipped") && userData?.Type === "Admin" && hasPermission(userData, "order.manage")) {
      props.optionTwo = "Add Details";
      props.optionTwoType = "primary";
      props.optionTwoClickFn = () => openInvoiceModal?.(row);
    }

    if (!hasTrackingNumber(TrackingNumber) && userData?.Type === "Admin" && hasPermission(userData, "order.manage")) {
      props.optionOne = "Add Tracking";
      props.optionOneType = "success";
      props.optionOneClickFn = () => openAddTrackingModal?.(row);
    }

    if (userData?.Type === "Admin" && hasPermission(userData, "order.delete")) {
      props.optionFour = "Delete";
      props.optionFourType = "failure";
      props.optionFourClickFn = () => {
        Swal.fire({
          title: "Are you sure?",
          text: "Deleting this order will also void the attached Invoice.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: colors["successColor"],
          cancelButtonColor: colors["failureColor"],
          confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
          if (result.isConfirmed) {
            let url = "";
            if (row.OrderType === "Direct Order" || row.OrderType === "DO") {
              url = "updatedirectorder?type=delete";
            } else if (
              row.OrderType === "Consignment Order" ||
              row.OrderType === "CO"
            ) {
              url = "updateconsignmentorder?type=delete";
            } else if (
              row.OrderType === "Product Usage" ||
              row.OrderType === "PU"
            ) {
              url = "updateproductusageorder?type=delete";
            }
            const apiServe = new APIService();
            const response = await apiServe.updateDataToBackend(
              { ID: row.ID },
              url
            );
            console.log("Response: ", response);
            if (response?.status == 200 || response?.status == 201) {
              Swal.fire({
                icon: "success",
                title: "Order Deleted Successfully",
                showConfirmButton: false,
                timer: 1500,
              });
              window.location.reload();
            } else {
              Swal.fire({
                icon: "error",
                title: "Error",
                text: "Unable to delete order",
              });
            }
          }
        });
      };
    }

    if (hasTrackingNumber(TrackingNumber) && OrderStatus !== "Completed") {
      props.optionOne = "Track Order";
      props.optionOneType = "primary";
      props.optionOneClickPath = `https://www.google.com/search?q=${TrackingNumber}`;
      props.openNewTab = true;
    }

    return props;
  };

  const tableHeadersDirectOrders = [
    {
      headerName: "PO Number",
      field: "PONumber",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.PONumber}
          path={`/DirectOrder/${params.data.ID}`}
        />
      ),
    },
    {
      headerName: "Order Date",
      field: "OrderDate",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Order Type",
      field: "OrderType",
      filter: true,
      sortable: false,
    },
    {
      headerName: "Ship To Account",
      field: "ShipToAccountName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.ShipToAccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Distributor",
      field: "DistributorName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.DistributorName}
          path={`/Distributor/${params.data.DistributorID}`}
        />
      ),
    },
    userData.Type === "Admin" ||
      userData.Type === "ASD" ||
      userData.Type === "Authorizer" ||
      userData.Type === "Viewer"
      ? {
        headerName: "Manufacturer",
        field: "ManufacturerName",
        filter: true,
        sortable: true,
        cellRenderer: (params) => (
          <TableLink
            value={params.data.ManufacturerName}
            path={`/Manufacturer/${params.data.ManufacturerID}`}
          />
        ),
      }
      : {
        headerName: "Manufacturer",
        field: "ManufacturerName",
        filter: true,
        sortable: true,
      },
    {
      headerName: "Order Total",
      field: "OrderTotal",
      filter: true,
      sortable: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Status",
      field: "Status",
      filter: true,
      sortable: false,
      cellRenderer: (params) => <StatusIndicator params={params} />,
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      minWidth: 150,
      cellRenderer: (params) => {
        const props = getTableOptionsCellProps(
          params.data,
          userData,
          openInvoiceModal,
          openAddTrackingModal
        );
        return <TableOptionsCellMenu {...props} />;
      },
    },
  ];

  const tableHeadersConsignmentOrders = [
    {
      headerName: "PO Number",
      field: "PONumber",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.PONumber}
          path={`/ConsignmentOrder/${params.data.ID}`}
        />
      ),
    },
    {
      headerName: "Order Date",
      field: "OrderDate",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Order Type",
      field: "OrderType",
      filter: true,
      sortable: false,
    },
    {
      headerName: "Ship To Account",
      field: "ShipToAccountName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.ShipToAccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Distributor",
      field: "DistributorName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.DistributorName}
          path={`/Distributor/${params.data.DistributorID}`}
        />
      ),
    },

    userData.Type === "Admin" ||
      userData.Type === "ASD" ||
      userData.Type === "Authorizer" ||
      userData.Type === "Viewer"
      ? {
        headerName: "Manufacturer",
        field: "ManufacturerName",
        filter: true,
        sortable: true,
        cellRenderer: (params) => (
          <TableLink
            value={params.data.ManufacturerName}
            path={`/Manufacturer/${params.data.ManufacturerID}`}
          />
        ),
      }
      : {
        headerName: "Manufacturer Name",
        field: "ManufacturerName",
        filter: true,
        sortable: true,
      },
    {
      headerName: "Order Total",
      field: "OrderTotal",
      filter: true,
      sortable: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Status",
      field: "Status",
      filter: true,
      sortable: false,
      cellRenderer: (params) => <StatusIndicator params={params} />,
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      minWidth: 150,
      cellRenderer: (params) => {
        const props = getTableOptionsCellProps(
          params.data,
          userData,
          openInvoiceModal,
          openAddTrackingModal
        );
        return <TableOptionsCellMenu {...props} />;
      },
    },
  ];

  const tableHeadersProductUsage = [
    {
      headerName: "PO Number",
      field: "PONumber",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.PONumber}
          path={`/ProductUsage/${params.data.ID}`}
        />
      ),
    },
    {
      headerName: "Order Date",
      field: "OrderDate",
      filter: true,
      sortable: true,
    },
    {
      headerName: "Order Type",
      field: "OrderType",
      filter: true,
      sortable: false,
    },
    {
      headerName: "Ship To Account",
      field: "ShipToAccountName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.ShipToAccountName}
          path={`/Account/${params.data.AccountID}`}
        />
      ),
    },
    {
      headerName: "Distributor",
      field: "DistributorName",
      filter: true,
      sortable: true,
      cellRenderer: (params) => (
        <TableLink
          value={params.data.DistributorName}
          path={`/Distributor/${params.data.DistributorID}`}
        />
      ),
    },

    userData.Type === "Admin" ||
      userData.Type === "ASD" ||
      userData.Type === "Authorizer" ||
      userData.Type === "Viewer"
      ? {
        headerName: "Manufacturer",
        field: "ManufacturerName",
        filter: true,
        sortable: true,
        cellRenderer: (params) => (
          <TableLink
            value={params.data.ManufacturerName}
            path={`/Manufacturer/${params.data.ManufacturerID}`}
          />
        ),
      }
      : {
        headerName: "Manufacturer",
        field: "ManufacturerName",
        filter: true,
        sortable: true,
      },
    {
      headerName: "Order Total",
      field: "OrderTotal",
      filter: true,
      sortable: true,
      cellDataType: "number",
      valueGetter: (params) =>
        parseFloat(String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")),
      cellRenderer: (params) => {
        const value = parseFloat(
          String(params.data.OrderTotal).replace(/[^0-9.-]+/g, "")
        );
        return `$${value.toLocaleString()}`;
      },
    },
    {
      headerName: "Status",
      field: "Status",
      filter: true,
      sortable: false,
      cellRenderer: (params) => <StatusIndicator params={params} />,
    },
    {
      headerName: "Action",
      field: "",
      sortable: false,
      minWidth: 150,
      cellRenderer: (params) => {
        const props = getTableOptionsCellProps(
          params.data,
          userData,
          openInvoiceModal,
          openAddTrackingModal
        );
        return <TableOptionsCellMenu {...props} />;
      },
    },
  ];

  const formatOrders = (orders) => {
    return (orders || []).map((order) => ({
      ...order,
      OrderDate: moment(order.OrderDate).format("MM-DD-YYYY"),
      OrderTotal: `$${parseFloat(order.OrderTotal).toLocaleString("en-US", {
        maximumFractionDigits: 2,
      })}`,
      OrderType:
        order.OrderType === "DO"
          ? "Direct Order"
          : order.OrderType === "CO"
            ? "Consignment Order"
            : order.OrderType === "PU"
              ? "Product Usage"
              : order.OrderType,
      OrderStatus: order.Status,
      Status: (
        <Button
          type={order.Status === "Completed" ? "primary" : "default"}
          shape="round"
          size="small"
          style={{
            color:
              order.Status === "Approved"
                ? "#27a907"
                : order.Status === "Shipped"
                ? colors["primaryColor"]
                : order.Status === "Completed"
                  ? "#fff"
                  : order.Status === "Payment Failed"
                    ? "red"
                  : order.Status === "Rejected"
                    ? "red"
                    : order.Status === "Pending"
                      ? "#ff9202"
                      : "default",
            borderColor:
              order.Status === "Approved"
                ? "#27a907"
                : order.Status === "Shipped"
                ? colors["primaryColor"]
                : order.Status === "Completed"
                  ? colors["primaryColor"]
                  : order.Status === "Payment Failed"
                    ? "red"
                  : order.Status === "Rejected"
                    ? "red"
                    : order.Status === "Pending"
                      ? "#ff9202"
                      : "default",
            background:
              order.Status === "Completed"
                ? colors["primaryColor"]
                : "transparent",
            width: "100%",
          }}
        >
          {order.Status}
        </Button>
      ),
    }));
  };

  const getEndpointForTab = (tabKey) => {
    switch (tabKey) {
      case "DirectOrders":
        return "getdirectorders";
      case "ConsignmentOrders":
        return "getconsignmentorders";
      case "ProductUsage":
        return "getproductusageorders";
      default:
        return "getdirectorders";
    }
  };

  const getOrderTypeForTab = (tabKey) => {
    switch (tabKey) {
      case "DirectOrders":
        return "DirectOrders";
      case "ConsignmentOrders":
        return "ConsignmentOrders";
      case "ProductUsage":
        return "ProductUsage";
      default:
        return "DirectOrders";
    }
  };

  const getStatusParam = () => {
    return selectedFilter ? selectedFilter : "All";
  };

  const appendSortParams = (params, tabKey) => {
    const sortState = sortStateByTab[tabKey];
    if (
      sortState?.field &&
      sortState?.order &&
      ORDER_SORT_FIELDS.has(sortState.field)
    ) {
      params.set("sortField", sortState.field);
      params.set("sortOrder", sortState.order);
    }
  };

  const buildOrdersUrl = (tabKey, page, pageSize) => {
    const params = new URLSearchParams();
    params.set("type", getStatusParam());
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    appendSortParams(params, tabKey);
    if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
    if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
    return `${getEndpointForTab(tabKey)}?${params.toString()}`;
  };

  const buildOrdersSearchUrl = (tabKey, keyword, page, pageSize) => {
    const params = new URLSearchParams();
    params.set("screen", "OrdersDashboard");
    params.set("word", keyword);
    params.set("orderType", getOrderTypeForTab(tabKey));
    params.set("page", String(page));
    params.set("pageSize", String(pageSize));
    appendSortParams(params, tabKey);
    if (selectedFilter) params.set("status", selectedFilter);
    if (dateRange?.startDate) params.set("startDate", dateRange.startDate);
    if (dateRange?.endDate) params.set("endDate", dateRange.endDate);
    return `search?${params.toString()}`;
  };

  const fetchOrdersForTab = async (tabKey, keywordOverride) => {
    const { page, pageSize } = pagination[tabKey];
    const keyword =
      typeof keywordOverride === "string" ? keywordOverride : searchKeyword;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const apiServe = new APIService();
      const response = await apiServe.getDataFromBackend(
        keyword
          ? buildOrdersSearchUrl(tabKey, keyword, page, pageSize)
          : buildOrdersUrl(tabKey, page, pageSize)
      );
      if (requestId !== requestIdRef.current) return;
      const rows = response?.data?.data ?? [];
      const formatted = formatOrders(rows);
      setTabData((prev) => ({
        ...prev,
        [tabKey]: formatted,
      }));
      const total = response?.data?.totalCount ?? rows.length;
      setPagination((prev) => ({
        ...prev,
        [tabKey]: {
          ...prev[tabKey],
          total,
        },
      }));
    } catch (err) {
      console.error("Failed to fetch orders:", err);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const activePage = pagination[activeTab]?.page ?? 1;
  const activePageSize = pagination[activeTab]?.pageSize ?? 20;
  const activeSort = sortStateByTab[activeTab];
  const [selectedFilter, setSelectedFilter] = React.useState("");

  React.useEffect(() => {
    fetchOrdersForTab(activeTab);
  }, [
    activeTab,
    activePage,
    activePageSize,
    selectedFilter,
    dateRange,
    searchKeyword,
    activeSort?.field,
    activeSort?.order,
  ]);

  const [isFilter, setIsFilter] = React.useState(false);

  const resetPagination = () => {
    setPagination((prev) => ({
      DirectOrders: { ...prev.DirectOrders, page: 1 },
      ConsignmentOrders: { ...prev.ConsignmentOrders, page: 1 },
      ProductUsage: { ...prev.ProductUsage, page: 1 },
    }));
  };

  const handleFilter = (filter) => {
    if (filter === "Clear") {
      setIsFilter(false);
      setSelectedFilter("");
      resetPagination();
      return;
    }
    setIsFilter(true);
    setSelectedFilter(filter);
    resetPagination();
  };

  const openInvoiceModal = (record) => {
    // console.log("Invoice Modal Opened: ", record);

    setOrderData(record);
    setShowInvoiceModal(true);
  };

  const openAddTrackingModal = (record) => {
    setOrderData(record);
    setShowAddTrackingModal(true);
  };

  const getTabRows = (tabKey) => {
    return tabData[tabKey] || [];
  };

  const handlePaginationChange = (tabKey, page, size) => {
    const current = pagination[tabKey];
    const nextSize = size || current.pageSize;
    const sizeChanged = nextSize !== current.pageSize;
    setPagination((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        page: sizeChanged ? 1 : page,
        pageSize: nextSize,
      },
    }));
  };

  const handleSortChange = (tabKey, nextSort) => {
    const normalizedSort =
      nextSort?.field &&
      nextSort?.order &&
      ORDER_SORT_FIELDS.has(nextSort.field)
        ? nextSort
        : null;
    const currentSort = sortStateByTab[tabKey];
    const isSameSort =
      currentSort?.field === normalizedSort?.field &&
      currentSort?.order === normalizedSort?.order;

    if (isSameSort) return;

    setSortStateByTab((prev) => ({
      ...prev,
      [tabKey]: normalizedSort,
    }));
    setPagination((prev) => ({
      ...prev,
      [tabKey]: {
        ...prev[tabKey],
        page: 1,
      },
    }));
  };

  const renderStatusFilterButtons = () =>
    ORDER_FILTERS.map((filter) => (
      <Button
        key={filter.value}
        ghost={selectedFilter !== filter.value}
        style={{ color: "#444", borderColor: "#444" }}
        onClick={() => handleFilter(filter.value)}
      >
        {filter.icon ? <Badge count={show ? filter.icon : 0} /> : null}
        {filter.label}
      </Button>
    ));

  const renderTabContent = (tabKey, columnDefs) => {
    const rows = getTabRows(tabKey);
    const pageInfo = pagination[tabKey];
    return (
      <>
        <Table
          rowData={rows}
          columnDefs={columnDefs}
          pagination={false}
          loading={loading}
          serverSideSort
          sortState={sortStateByTab[tabKey]}
          onSortChange={(sortState) => handleSortChange(tabKey, sortState)}
        />
        {!loading && pageInfo.total > 0 && (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Pagination
              current={pageInfo.page}
              pageSize={pageInfo.pageSize}
              total={pageInfo.total}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={["20", "50", "100"]}
              onChange={(page, size) =>
                handlePaginationChange(tabKey, page, size)
              }
              onShowSizeChange={(page, size) =>
                handlePaginationChange(tabKey, page, size)
              }
              showTotal={(total, range) =>
                `${range[0]}-${range[1]} of ${total}`
              }
            />
          </div>
        )}
      </>
    );
  };

  const tabPanes = [
    {
      key: "DirectOrders",
      // title: "Direct Orders",
      // icon: "",
      // content: (
      //   <Table
      //     // loading={loading}
      //     // Search={Input.Search}
      //     dataSource={directOrders}
      //     columns={columns}
      //     components={components}
      //     style={{ marginTop: '-15px' }}
      //     pagination={{
      //       pageSize: pageSize,
      //       total: totalCount,
      //       showSizeChanger: true,
      //       onShowSizeChange: async (cur, ps) => await handleShowSizeChange(cur, ps),
      //       // onChange: async (page, ps) => {
      //       //   console.log('Page Changed: ', page);
      //       //   const curDataCount = page * ps;
      //       //   if (curDataCount > formattedInvoices.length) {
      //       //     await getPaginatedInvoices(page, ps)
      //       //   }
      //       // }
      //     }}
      //   />
      // ),
      content: renderTabContent("DirectOrders", tableHeadersDirectOrders),
      // count: directOrders.length,
    },
    {
      key: "ConsignmentOrders",
      // title: "Consignment Orders",
      // icon: "",
      // content: (
      //   <Table
      //     // loading={loading}
      //     dataSource={consignmentOrders}
      //     columns={columns}
      //     components={components}
      //     style={{ marginTop: '-15px' }}
      //     pagination={{
      //       pageSize: pageSize,
      //       total: totalCount,
      //       showSizeChanger: true,
      //       onShowSizeChange: async (cur, ps) => await handleShowSizeChange(cur, ps),
      //       // onChange: async (page, ps) => {
      //       //   console.log('Page Changed: ', page);
      //       //   const curDataCount = page * ps;
      //       //   if (curDataCount > formattedInvoices.length) {
      //       //     await getPaginatedInvoices(page, ps)
      //       //   }
      //       // }
      //     }}
      //   />
      // ),
      content: renderTabContent(
        "ConsignmentOrders",
        tableHeadersConsignmentOrders
      ),
      // count: consignmentOrders.length,
    },
    {
      key: "ProductUsage",
      // title: "Product Usage",
      // icon: "",
      // content: (
      //   <Table
      //     // loading={loading}
      //     dataSource={productUsage}
      //     columns={columns}
      //     components={components}
      //     style={{ marginTop: '-15px' }}
      //     pagination={{
      //       pageSize: pageSize,
      //       total: totalCount,
      //       showSizeChanger: true,
      //       onShowSizeChange: async (cur, ps) => await handleShowSizeChange(cur, ps),
      //       // onChange: async (page, ps) => {
      //       //   console.log('Page Changed: ', page);
      //       //   const curDataCount = page * ps;
      //       //   if (curDataCount > formattedInvoices.length) {
      //       //     await getPaginatedInvoices(page, ps)
      //       //   }
      //       // }
      //     }}
      //   />
      // ),
      content: renderTabContent("ProductUsage", tableHeadersProductUsage),
      // count: productUsage.length,
    },
  ];

  const changeTab = (key) => {
    setActiveTab(key);
  };
  const handleDateFilterChange = async (dateRange) => {
    setDateRange(dateRange);
    resetPagination();
  };

  const handleSearch = (value) => {
    const keyword = (value || "").trim();
    setSearchKeyword(keyword);
    resetPagination();
  };

  const renderedInvoiceNumber = pagination[activeTab]?.total ?? 0;

  const closeModal = () => setShowInvoiceModal(false);

  return (
    <div>
      {/* <CustomHeader leftTopText="Welcome back!" leftBottomText="Orders" leftBottomExtraText={`${formattedOrders.length}`}
        rightTopText="Logged in as Admin" rightBottomText="User" /> */}
      <TitleBar
        label="Orders"
        invoiceNumber={renderedInvoiceNumber}
        filterBarComponents={
          <>
            <CommonButton
              variant="timeframe"
              isActive={activeTab === "DirectOrders"}
              onClick={() => changeTab("DirectOrders")}
            >
              Direct Orders
            </CommonButton>
            <CommonButton
              variant="timeframe"
              isActive={activeTab === "ConsignmentOrders"}
              onClick={() => changeTab("ConsignmentOrders")}
            >
              Consignment Orders
            </CommonButton>
            <CommonButton
              variant="timeframe"
              isActive={activeTab === "ProductUsage"}
              onClick={() => changeTab("ProductUsage")}
            >
              Product Usage
            </CommonButton>
          </>
        }
        rightFilterBarComponents={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Input.Search
              placeholder="Search"
              // suffix={<SearchOutlined />}
              allowClear
              style={{ height: "36px" }}
              // onSearch={value => setSearchKeyword(value)}
              onChange={(e) => {
                if (e.target.value === "") {
                  handleSearch("");
                }
              }}
              onSearch={(value) => handleSearch(value)}
            />
          </div>
        }
      />
      {isPhone ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <div>
            <DateRangeFilter
              onFilterChange={handleDateFilterChange}
              labelText="Filter by date"
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              overflowX: "scroll",
              gap: "16px",
              scrollbarWidth: "none",
            }}
          >
            {renderStatusFilterButtons()}
            {isFilter && (
              <Button
                ghost
                style={{ color: "#444", borderColor: "#444" }}
                onClick={() => handleFilter("Clear")}
              >
                Clear Filters <FilterOutlined />
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            margin: "30px 0px 10px 0px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "20px",
            }}
          >
            {/* <Button icon={<PlusOutlined />} iconPosition='end' ghost style={{ color: '#444', borderColor: '#444' }}>Set Date Range</Button>
          {/* <Button icon={<PlusOutlined />} iconPosition='end' ghost style={{ color: '#444', borderColor: '#444' }}>Add Filter</Button> */}
            <DateRangeFilter
              onFilterChange={handleDateFilterChange}
              labelText="Filter by date"
            />
            {renderStatusFilterButtons()}
            {isFilter && (
              <Button
                ghost
                style={{ color: "#444", borderColor: "#444" }}
                onClick={() => handleFilter("Clear")}
              >
                Clear Filters <FilterOutlined />
              </Button>
            )}
          </div>
        </div>
      )}
      <Tabs activeKey={activeTab} onChange={changeTab}>
        {tabPanes.map((pane) => (
          <TabPane
            key={pane.key}
          // tab={
          //   <span>
          //     {/* <span>
          //       {pane.icon ? <Icon type={pane.icon || null} /> : null}
          //       {pane.title}
          //     </span> */}
          //     {/* <span
          //       style={{
          //         color: "#B0B0B0",
          //         fontWeight: "300",
          //         marginLeft: "3px",
          //         fontSize: "12px",
          //       }}
          //     >
          //       ({pane.count})
          //     </span> */}
          //   </span>
          // }
          >
            <TabFade activeKey={activeTab}>{pane.content}</TabFade>
          </TabPane>
        ))}
      </Tabs>

      {/* <Table
        // loading={loading}
        dataSource={formattedOrders}
        columns={columns}
        components={components}
        pagination={{
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          onShowSizeChange: async (cur, ps) => await handleShowSizeChange(cur, ps),
          // onChange: async (page, ps) => {
          //   console.log('Page Changed: ', page);
          //   const curDataCount = page * ps;
          //   if (curDataCount > formattedInvoices.length) {
          //     await getPaginatedInvoices(page, ps)
          //   }
          // }
        }}
      /> */}
      {showInvoiceModal && (
        <div>
          <InvoiceFormNew orderType={orderData.OrderType} orderData={orderData} showInvoiceModal={showInvoiceModal} onClose={closeModal} />
        </div>
      )}
      {showAddTrackingModal && (
        <AddTrackingModal
          isOpen={showAddTrackingModal}
          onClose={() => setShowAddTrackingModal(false)}
          orderType={orderData.OrderType}
          orderData={orderData}
        />
      )}
    </div>
  );
};

export default Orders;
